/**
 * Contact form submission — wraps the `backendContactRequest` callable.
 *
 * Backend source of truth:
 * `home-ceo-backend/functions/typescript/src/callables/backend-contact-request.ts`.
 * The function writes a doc to `mail/{auto-id}` shaped for the
 * `firestore-send-email` extension, which dispatches via SendGrid.
 *
 * `success: true` means the doc was queued, not that the email was sent.
 * Delivery is asynchronous and there's no client-visible confirmation.
 *
 * Request and response types come from
 * `@latlonglabs/home-ceo-backend-contracts` so backend shape changes
 * surface as a typecheck failure here, not a silent runtime mismatch.
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import type {
  BackendContactRequestData,
  BackendContactRequestResponse,
} from '@latlonglabs/home-ceo-backend-contracts';
import { app } from '@config/firebase-config';

const functions = getFunctions(app, 'us-west1');

export async function submitContactForm(
  input: BackendContactRequestData,
): Promise<void> {
  const fn = httpsCallable<
    BackendContactRequestData,
    BackendContactRequestResponse
  >(functions, 'backendContactRequest');
  const result = await fn(input);
  if (!result.data.success) {
    // Defensive — backend always returns success:true on the success path;
    // failures come back as HttpsError. This branch is unreachable today.
    throw new Error('Contact submission did not succeed');
  }
}
